export type Side = "LEFT" | "RIGHT" | "CENTER";
export type Line =
  | "GOALKEEPER"
  | "DEFENDER"
  | "MIDFIELD"
  | "ATTACKING_MIDFIELD"
  | "FORWARD";

export interface PositionCode {
  raw: string;
  side: Side;
  line: Line;
  role: string;
}

export function parsePositionCode(raw: string): PositionCode {
  const normalized = normalizeRaw(raw);
  const tokens = tokenize(normalized);

  const side = extractSide(tokens) ?? "CENTER";
  const line = extractLine(tokens) ?? fallbackLine(tokens);
  const role = deriveRole(tokens, side, line);

  if (role === "UNKNOWN" || !line || tokens.length === 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[position-parser] Unrecognized position "${raw}" → { side: "${side}", line: "${line}", role: "${role}" }`
    );
  }

  return { raw, side, line, role };
}



function normalizeRaw(input: string): string {
  const s = (input || "")
    .trim()
    .toUpperCase()
    .replace(/[–—\- ]+/g, "_")
    .replace(/__+/g, "_")
    .replace(/^_+|_+$/g, "");
  return s;
}

function tokenize(normalized: string): string[] {
  if (!normalized) return [];
  return normalized.split("_").filter(Boolean);
}

function extractSide(tokens: string[]): Side | null {
  if (tokens.includes("LEFT")) return "LEFT";
  if (tokens.includes("RIGHT")) return "RIGHT";
  if (tokens.includes("CENTER")) return "CENTER";
  return null;
}

function extractLine(tokens: string[]): Line | null {
  if (tokens.includes("GOALKEEPER") || tokens.includes("GK")) return "GOALKEEPER";

  const hasAtt = tokens.includes("ATTACKING") || tokens.includes("ATTACKINGMIDFIELD");
  const hasMid = tokens.includes("MIDFIELD") || tokens.includes("MIDFIELDER");
  if (hasAtt && hasMid) return "ATTACKING_MIDFIELD";

  if (tokens.some((t) => ["DEFENSE", "DEFENDER", "CB", "FB", "WB"].includes(t)))
    return "DEFENDER";

  if (hasMid) return "MIDFIELD";

  if (tokens.some((t) => ["FORWARD", "STRIKER", "ATTACKER", "CF", "WF"].includes(t)))
    return "FORWARD";

  return null;
}

function fallbackLine(tokens: string[]): Line {
  const set = new Set(tokens);
  if (set.has("WINGBACK") || set.has("FULLBACK") || set.has("CENTERBACK")) return "DEFENDER";
  if (set.has("WINGER") || set.has("STRIKER") || set.has("FORWARD")) return "FORWARD";
  if (set.has("ATTACKING")) return "FORWARD";
  return "MIDFIELD";
}

function deriveRole(tokens: string[], side: Side, line: Line): string {
  const filtered = tokens
    .filter((t) => !isSideToken(t))
    .filter((t) => !isLineToken(t));

  const mapped = filtered.map(normalizeRoleToken);

  if (mapped.length === 0) {
    switch (line) {
      case "GOALKEEPER": return "GOALKEEPER";
      case "DEFENDER": return side === "CENTER" ? "CENTERBACK" : "FULLBACK";
      case "ATTACKING_MIDFIELD": return "ATTACKING_MIDFIELDER";
      case "MIDFIELD": return side === "CENTER" ? "CENTRAL_MIDFIELDER" : "WIDE_MIDFIELDER";
      case "FORWARD": return side === "CENTER" ? "CENTER_FORWARD" : "WINGER";
    }
  }

  if (line === "FORWARD" && side === "CENTER" && containsAny(mapped, ["FORWARD", "STRIKER", "ATTACKER", "CF"]))
    return "CENTER_FORWARD";

  if (line === "DEFENDER" && containsAny(mapped, ["WINGBACK", "WB"]))
    return "WINGBACK";

  if (line === "DEFENDER" && containsAny(mapped, ["FULLBACK", "FB"]))
    return "FULLBACK";

  if (line === "DEFENDER" && containsAny(mapped, ["CENTERBACK", "CB"]))
    return "CENTERBACK";

  if (line === "FORWARD" && containsAny(mapped, ["WINGER", "WF"]))
    return "WINGER";

  if (line === "ATTACKING_MIDFIELD" || containsAny(mapped, ["AM", "ATTACKING_MIDFIELD"]))
    return "ATTACKING_MIDFIELDER";

  if (line === "MIDFIELD") {
    if (containsAny(mapped, ["DEFENSIVE", "DM"])) return "DEFENSIVE_MIDFIELDER";
    if (containsAny(mapped, ["CENTRAL", "CM"])) return "CENTRAL_MIDFIELDER";
    if (containsAny(mapped, ["WIDE"])) return "WIDE_MIDFIELDER";
    if (containsAny(mapped, ["BOX", "TO", "BOX-TO-BOX", "BOXTOBOX", "B2B"])) return "BOX_TO_BOX_MIDFIELDER";
  }

  const role = mapped.join("_");
  return role || "UNKNOWN";
}

/* Utility helpers */

function isSideToken(t: string): boolean {
  return t === "LEFT" || t === "RIGHT" || t === "CENTER";
}

function isLineToken(t: string): boolean {
  return (
    t === "GOALKEEPER" || t === "GK" ||
    t === "DEFENDER" || t === "DEFENSE" || t === "CB" || t === "FB" || t === "WB" ||
    t === "MIDFIELD" || t === "MIDFIELDER" ||
    t === "ATTACKING" || t === "ATTACKINGMIDFIELD" || t === "ATTACKING-MIDFIELD" ||
    t === "FORWARD" || t === "STRIKER" || t === "ATTACKER" || t === "CF" || t === "WF"
  );
}

function normalizeRoleToken(t: string): string {
  switch (t) {
    case "FULL_BACK": case "FULLBACK": case "FB": return "FULLBACK";
    case "WING_BACK": case "WINGBACK": case "WB": return "WINGBACK";
    case "CENTER_BACK": case "CENTRE_BACK": case "CENTERBACK": case "CB": return "CENTERBACK";
    case "CENTRAL": case "CENTRE": return "CENTRAL";
    case "DEFENSIVE": case "HOLDING": case "ANCHOR": return "DEFENSIVE";
    case "ATTACKINGMIDFIELD": case "ATTACKING-MIDFIELD": return "ATTACKING_MIDFIELD";
    case "ATTACKINGMIDFIELDER": return "ATTACKING_MIDFIELDER";
    case "MIDFIELDER": return "MIDFIELD";
    case "STRIKER": case "ATTACKER": case "CF": return "FORWARD";
    case "WINGER": case "WF": return "WINGER";
    case "INSIDE_FORWARD": case "INSIDEFORWARD": return "INSIDE_FORWARD";
    case "SECOND_STRIKER": case "SECOND": case "SS": return "SECOND_STRIKER";
    case "BOX-TO-BOX": case "BOXTOBOX": case "B2B": return "BOX_TO_BOX";
    case "GOALKEEPER": case "GK": return "GOALKEEPER";
    default: return t;
  }
}

function containsAny(tokens: string[], anyOf: string[]): boolean {
  const set = new Set(tokens);
  return anyOf.some((a) => set.has(a));
}

